import React, { useState } from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import DangerousOutlinedIcon from '@mui/icons-material/DangerousOutlined';
import { Box, Typography } from '@mui/material';
import type { AppointmentResponse } from '@store/generated/appointments';
import { useSnackbar } from 'notistack';

import { useMarkNoShowMutation } from '../../store';
import { ActionLink } from '../AppointmentCard/components/ActionLink';

export interface NoShowActionProps {
  appointment: AppointmentResponse;
  onMarkNoShowClick?: () => void;
}

/**
 * Reusable component for marking appointments as no-show
 * Shows ActionLink when not marked, icon + text when marked
 * Consumer is responsible for analytics tracking via onMarkNoShowClick callback
 */
export const NoShowAction: React.FC<NoShowActionProps> = ({
  appointment,
  onMarkNoShowClick,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [markNoShow] = useMarkNoShowMutation();
  const [showNoShowDialog, setShowNoShowDialog] = useState(false);

  // Handle mark no-show click - shows confirmation dialog
  const handleMarkNoShowClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Call analytics callback if provided (consumer handles tracking)
    onMarkNoShowClick?.();

    setShowNoShowDialog(true);
  };

  // Perform the actual no-show marking after confirmation
  const performMarkNoShow = async () => {
    try {
      await markNoShow({ appointmentId: appointment.appointment_id }).unwrap();
      enqueueSnackbar('Appointment marked as no-show', { variant: 'success' });
      setShowNoShowDialog(false);
    } catch (error) {
      console.error('Failed to mark appointment as no-show:', error);
      enqueueSnackbar('Failed to mark appointment as no-show. Please try again.', {
        variant: 'error',
      });
      setShowNoShowDialog(false);
    }
  };

  // Don't show for suggested appointments
  if (appointment.status === 'suggested') {
    return null;
  }

  // If already marked as no-show, show read-only status
  if (appointment.no_showed === true) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <DangerousOutlinedIcon sx={{ fontSize: 18, color: '#D11A9A' }} />
        <Typography variant="body2" sx={{ color: '#D11A9A' }}>
          No Show
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ActionLink label="Mark 'No Show'" onClick={handleMarkNoShowClick} />

      <ConfirmDialog
        open={showNoShowDialog}
        variant="warning"
        title="Mark as No-Show?"
        message={
          <>
            This will mark the appointment as a no-show.
            <br />
            This action cannot be undone.
          </>
        }
        onClose={() => setShowNoShowDialog(false)}
        onConfirm={performMarkNoShow}
        confirmText="Yes, Mark No-Show"
      />
    </>
  );
};
